"use client"
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface Notification {
  _id?: string;
  userId?: string;
  type: 'blog-scheduled' | 'blog-published' | 'blog-draft' | 'system' | 'user-activity';
  title: string;
  message: string;
  timestamp: string | Date;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  category: string;
  relatedId?: string;
  metadata?: {
    author?: string;
    scheduledDate?: string;
    publishDate?: string;
    tags?: string[];
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: (limit?: number) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  hasNewNotification: boolean;
  clearNewNotificationFlag: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const previousUnreadCountRef = useRef(0);

  const fetchNotifications = useCallback(async (limit?: number) => {
    setLoading(true);
    try {
      const url = limit
        ? `/api/admin/notifications?limit=${limit}`
        : '/api/admin/notifications';
      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized: Please log in again');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate response data
      if (!Array.isArray(data)) {
        console.error('Invalid response format: expected array');
        return;
      }

      const newUnreadCount = data.filter((n: Notification) => !n.isRead).length;
      const previousUnreadCount = previousUnreadCountRef.current;

      // Check if there's a new notification
      if (newUnreadCount > previousUnreadCount && previousUnreadCount > 0) {
        setHasNewNotification(true);
      }

      previousUnreadCountRef.current = newUnreadCount;
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Don't clear existing notifications on error, just log it
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    // Check if this is an activity-based notification (not stored in DB)
    const isActivityNotification = id.startsWith('activity_');

    // Optimistic update
    setNotifications(prev =>
      prev.map(notification =>
        notification._id === id ? { ...notification, isRead: true } : notification
      )
    );

    // For activity notifications, just update locally (no API call needed)
    if (isActivityNotification) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/notifications/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isRead: true }),
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert optimistic update on error
        setNotifications(prev =>
          prev.map(notification =>
            notification._id === id ? { ...notification, isRead: false } : notification
          )
        );
        const errorData = await response.json();
        console.error('Error marking notification as read:', errorData.error || 'Unknown error');
      }
    } catch (error) {
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(notification =>
          notification._id === id ? { ...notification, isRead: false } : notification
        )
      );
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    const previousNotifications = notifications;
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, isRead: true }))
    );

    try {
      const response = await fetch('/api/admin/notifications/mark-all-read', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert optimistic update on error
        setNotifications(previousNotifications);
        const errorData = await response.json();
        console.error('Error marking all notifications as read:', errorData.error || 'Unknown error');
      }
    } catch (error) {
      // Revert optimistic update on error
      setNotifications(previousNotifications);
      console.error('Error marking all notifications as read:', error);
    }
  }, [notifications]);

  const deleteNotification = useCallback(async (id: string) => {
    // Check if this is an activity-based notification (not stored in DB)
    const isActivityNotification = id.startsWith('activity_');

    // Store the notification for potential rollback
    const deletedNotification = notifications.find(n => n._id === id);

    // Optimistic update
    setNotifications(prev => prev.filter(notification => notification._id !== id));

    // For activity notifications, just remove locally (no API call needed)
    if (isActivityNotification) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/notifications/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert optimistic update on error
        if (deletedNotification) {
          setNotifications(prev => [...prev, deletedNotification].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          ));
        }
        const errorData = await response.json();
        console.error('Error deleting notification:', errorData.error || 'Unknown error');
      }
    } catch (error) {
      // Revert optimistic update on error
      if (deletedNotification) {
        setNotifications(prev => [...prev, deletedNotification].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ));
      }
      console.error('Error deleting notification:', error);
    }
  }, [notifications]);

  const clearNewNotificationFlag = useCallback(() => {
    setHasNewNotification(false);
  }, []);

  // Fetch notifications on mount and poll every 30 seconds
  useEffect(() => {
    // Only fetch if we're in the admin area (not on login page)
    if (typeof window !== 'undefined' && window.location.pathname !== '/admin/login') {
      const timeout = window.setTimeout(() => {
        fetchNotifications();
      }, 0);
      const interval = setInterval(() => {
        fetchNotifications();
      }, 30000); // Poll every 30 seconds

      return () => {
        window.clearTimeout(timeout);
        clearInterval(interval);
      };
    }
    return undefined;
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        hasNewNotification,
        clearNewNotificationFlag,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

